import { useRef, useEffect } from 'react';

/**
 * Custom hook for adding drag-to-scroll functionality to any scrollable element
 * Based on working jQuery implementation
 */
export const useDragToScroll = (options = {}) => {
    const {
        horizontal = true,
        vertical = true
    } = options;

    const elementRef = useRef(null);
    const cleanupRef = useRef(null);
    const isDragging = useRef(false);
    const lastPos = useRef({ x: 0, y: 0 });

    // Function to setup drag-to-scroll on an element
    const setupDragToScroll = (element) => {
        if (!element) return;

        const handleMouseDown = (e) => {
            // Only start dragging on left mouse button
            if (e.button !== 0) return;
            
            // Don't interfere with form elements
            if (['INPUT', 'SELECT', 'TEXTAREA', 'BUTTON', 'A'].includes(e.target.tagName)) {
                return;
            }

            isDragging.current = true;
            lastPos.current = { x: e.pageX, y: e.pageY };
            
            element.style.cursor = 'grabbing';
            element.style.userSelect = 'none';
            document.body.style.userSelect = 'none';
            
            e.preventDefault();
        };

        const handleMouseMove = (e) => {
            if (!isDragging.current) return;
            
            e.preventDefault();
            
            // Calculate delta from last position (like the jQuery example)
            const currentX = e.pageX;
            const currentY = e.pageY;
            const deltaX = currentX - lastPos.current.x;
            const deltaY = currentY - lastPos.current.y;
           
            
            // Apply scroll changes (subtract delta like in jQuery example)
            if (horizontal) {
                const oldScrollLeft = element.scrollLeft;
                const maxScrollLeft = element.scrollWidth - element.clientWidth;
                
                // Fix: Reverse delta direction (drag right = scroll left = subtract delta)
                const newScrollLeft = Math.max(0, Math.min(oldScrollLeft - deltaX, maxScrollLeft));
                
              
                
                // Temporarily disable smooth scrolling for programmatic scroll
                const originalScrollBehavior = element.style.scrollBehavior;
                element.style.scrollBehavior = 'auto';
                
                element.scrollLeft = newScrollLeft;
                
                // Restore original scroll behavior
                element.style.scrollBehavior = originalScrollBehavior;
                
            }
            
            if (vertical) {
                const oldScrollTop = element.scrollTop;
                const maxScrollTop = element.scrollHeight - element.clientHeight;
                const newScrollTop = Math.max(0, Math.min(oldScrollTop - deltaY, maxScrollTop));
                
                // Temporarily disable smooth scrolling for programmatic scroll
                const originalScrollBehavior = element.style.scrollBehavior;
                element.style.scrollBehavior = 'auto';
                
                element.scrollTop = newScrollTop;
                
                // Restore original scroll behavior
                element.style.scrollBehavior = originalScrollBehavior;
                
            }
            
            // Update last position for next move
            lastPos.current = { x: currentX, y: currentY };
        };

        const handleMouseUp = () => {
            if (!isDragging.current) return;
           
            isDragging.current = false;
            element.style.cursor = 'grab';
            element.style.userSelect = '';
            document.body.style.userSelect = '';
        };

        const handleMouseLeave = () => {
            if (isDragging.current) {
                console.log('DragToScroll: Mouse leave - ending drag');
                handleMouseUp();
            }
        };

        // Set initial cursor
        element.style.cursor = 'grab';

        // Add event listeners
        element.addEventListener('mousedown', handleMouseDown);
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        element.addEventListener('mouseleave', handleMouseLeave);

        // Return cleanup function
        return () => {
            element.removeEventListener('mousedown', handleMouseDown);
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            element.removeEventListener('mouseleave', handleMouseLeave);
            
            // Reset styles
            element.style.cursor = '';
            element.style.userSelect = '';
            document.body.style.userSelect = '';
        };
    };

    // Custom ref callback that sets up drag-to-scroll when element is available
    const refCallback = (element) => {
        // Cleanup previous setup
        if (cleanupRef.current) {
            cleanupRef.current();
            cleanupRef.current = null;
        }

        elementRef.current = element;

        if (element) {
            cleanupRef.current = setupDragToScroll(element);
        } 
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (cleanupRef.current) {
                console.log('DragToScroll: Component unmounting - cleaning up');
                cleanupRef.current();
            }
        };
    }, []);

    return refCallback;
};